/**
 * Smoothly scrolls to the element matching window.location.hash on mount
 * AND whenever the hash changes.
 *
 * Mounted at the top of <Site /> so it covers:
 *   - First-load on /#about (e.g. someone shares an anchor link)
 *   - SPA navigation arriving at /#about from another route
 *   - Old-URL redirects arriving via <Navigate to="/#about" replace />
 *
 * Re-scrolls after a delay to correct for layout shift caused by lazy-loaded
 * gallery images (otherwise clicking "book the studio" can land mid-gallery
 * because images below the fold load AFTER the initial scroll completes,
 * pushing the contact section further down).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function ScrollToHash() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;

    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // First scroll — immediate, after layout settles
    requestAnimationFrame(() => requestAnimationFrame(scroll));

    // Correction scrolls — re-fire after images / fonts have loaded.
    // Without these, clicking "book the studio" from the top of the page
    // scrolls to where #contact USED to be, then lazy-loaded gallery images
    // push the real #contact further down the page.
    const t1 = setTimeout(scroll, 600);
    const t2 = setTimeout(scroll, 1400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hash]);

  return null;
}
